// src/components/DesignationForm.tsx
import React, { useState, useEffect } from 'react';
import { X, Shield, AlertCircle } from 'lucide-react';
import { Designation } from '../types/designation';

interface DesignationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: Designation | null;
  isLoading?: boolean;
}

export function DesignationForm({ isOpen, onClose, onSubmit, initialData, isLoading }: DesignationFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Check if designation is system default
  const isSystemDesignation = initialData?.is_default === true || 
                              initialData?.created_by === 'system';

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSystemDesignation) {
      return; // Prevent submission for system designations
    }
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-300">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {initialData ? 'Edit Designation' : 'Add New Designation'}
            </h3>
            {isSystemDesignation && (
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                System Default Designation
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Warning for system designations */}
          {isSystemDesignation && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">System designation protection</p>
                  <p className="text-xs mt-1">System default designations cannot be edited or deleted. This ensures system integrity.</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Designation Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSystemDesignation}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                isSystemDesignation ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="e.g., Software Engineer, HR Manager, Sales Executive"
              autoFocus={!isSystemDesignation}
            />
            {isSystemDesignation && (
              <p className="text-xs text-gray-500 mt-1">
                System designations cannot be renamed
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              disabled={isSystemDesignation}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
                isSystemDesignation ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Brief description of the designation..."
            />
          </div>

          {/* Designation metadata for system designations */}
          {isSystemDesignation && initialData && (
            <div className="pt-2 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                <div>
                  <span className="font-medium">Created:</span>
                  <p>{initialData.created_at ? new Date(initialData.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium">Created By:</span>
                  <p>{initialData.created_by === 'system' ? 'System' : initialData.created_by?.substring(0, 8) + '...'}</p>
                </div>
                {initialData.updated_at && (
                  <div className="col-span-2">
                    <span className="font-medium">Last Updated:</span>
                    <p>{new Date(initialData.updated_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {!isSystemDesignation && (
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : initialData ? 'Update Designation' : 'Create Designation'}
              </button>
            )}
            {isSystemDesignation && (
              <button
                type="button"
                disabled={true}
                className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
              >
                Protected
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}